/**
 * Shared progress types for agent progress tracking.
 * Used by both Deep Research and Data Capture agents.
 */

export enum ToolCallStatus {
  /** Realtime snapshot not received yet; execution phase unknown. */
  UNKNOWN = 'unknown',
  /** Waiting to start in the current batch. */
  QUEUED = 'queued',
  /** Currently executing. */
  RUNNING = 'running',
  /** Finished with a result. */
  COMPLETED = 'completed',
  /** Tool call was skipped because the user stopped the assistant before it ran. */
  CANCELLED = 'cancelled',
}

/**
 * Persisted placeholder when a tool did not run because the user stopped the assistant.
 * Keep in sync with benchling.llm.util.pending_tool_results.INTERRUPT_SKIPPED_TOOL_RESULT_TEXT.
 */
export const INTERRUPT_SKIPPED_TOOL_RESULT_TEXT = 'User requested the agent to stop. Tool was not executed.';

/**
 * Prior INTERRUPT_SKIPPED_TOOL_RESULT_TEXT values. When changing the constant above after ship,
 * append the old value here so the UI still treats existing persisted placeholders as skipped.
 * (Backend interrupt cleanup uses in-memory tool call ids, not this text.)
 */
export const LEGACY_INTERRUPT_SKIPPED_TOOL_RESULT_TEXTS: ReadonlyArray<string> = [];

/** Suffix for skipped tools in the thinking tool list, e.g. "Running code (skipped)". */
export const SKIPPED_TOOL_DISPLAY_LABEL = '(skipped)';

/** Suffix for tools not started yet in the current batch, e.g. "Running code (queued)". */
export const QUEUED_TOOL_DISPLAY_LABEL = '(queued)';

/** All persisted skip-placeholder strings (current + legacy). Used by isInterruptSkippedToolResult. */
export const ALL_INTERRUPT_SKIPPED_TOOL_RESULT_TEXTS: ReadonlyArray<string> = [
  INTERRUPT_SKIPPED_TOOL_RESULT_TEXT,
  ...LEGACY_INTERRUPT_SKIPPED_TOOL_RESULT_TEXTS,
];

/**
 * Represents a tool call in progress or completed.
 * This is the base interface used by both Deep Research and Data Capture.
 */
export interface ProgressToolCall {
  /** The tool name (can be an enum value or string for external tools) */
  tool: string;
  /** Unique identifier for this tool call */
  toolId: string;
  /** When the tool call was initiated */
  createdAt: Date;
  /** Current status of the tool call for progress rendering. */
  status: ToolCallStatus;
  /** Human-readable display name for the tool */
  displayName: string;
  /** Optional tool arguments (used by Data Capture for detail display) */
  args?: Record<string, unknown>;
  /** Optional response text from the tool (used by Data Capture for detail display) */
  responseText?: string;
}

/**
 * Progress data containing tool call history.
 * Used for both in-progress and completed thinking sections.
 */
export interface ProgressData {
  toolCalls: Array<ProgressToolCall>;
}
